// @flow

import { useCallback, useEffect } from 'react';

import { useNavigate } from 'react-router-dom';
import { compose } from 'redux';

import { Loader, View } from 'reshaped';

import { withPageMeta } from '@gandi/app-runner';

import { needsAuth } from '@gandi/authentication';

import {
  selectIsFailureCartOwnerList,
  selectIsLoadedCartOwnerList,
  setCartUUID,
  setCartOwner,
} from '@gandi/cart';
import { selectCurrentId } from '@gandi/redux-sharing';

import { localeSelector, useTranslator } from '@gandi/translate';

import { Layout } from 'src/components/Layout';
import { withRouter, useQuery } from 'src/helpers/withRouter';
import type { Params, Query } from 'src/helpers/withRouter';
import { useAppSelector } from 'src/hooks/useAppSelector';
import { useFeatureFlag } from 'src/hooks/useFeatureFlag';
import type { FullState, PreFetchArgs } from 'src/types';

import { AccountChoice } from './AccountChoice';
import Presenter, { type Props, selectFirstPurchaseUrl } from './Presenter';
import {
  selectRedirectToNonMinimal,
  selectResellerOwnerId,
  selectShouldRedirectToAccountForm,
  selectShouldRedirectToResellerOwnerId,
} from './modules/selectors';

export function DecorateContent(props: Props): React.MixedElement | null {
  const { __ } = useTranslator();

  const displayNewAccountChoice = useFeatureFlag('FF_SHOP_ACCOUNT_CHOICE');

  const isFailureCartOwners = useAppSelector(selectIsFailureCartOwnerList);
  const isLoadedCartOwners = useAppSelector(selectIsLoadedCartOwnerList);

  const { firstBuy } = useQuery<Query>();

  const navigate = useNavigate();

  const redirectToAccountForm = useAppSelector(
    selectShouldRedirectToAccountForm,
  );

  const firstPurchaseUrl = useAppSelector(selectFirstPurchaseUrl);
  const handleRedirectToAccountForm = useCallback(() => {
    navigate(firstPurchaseUrl, {
      replace: true,
    });
  }, [firstPurchaseUrl, navigate]);

  useEffect(() => {
    if (!firstBuy && redirectToAccountForm) {
      handleRedirectToAccountForm();
    }
  }, [firstBuy, handleRedirectToAccountForm, redirectToAccountForm]);

  if (!firstBuy && redirectToAccountForm) return null;

  if (!isLoadedCartOwners && !isFailureCartOwners)
    return (
      <Layout backgroundColor="surface" showCartSummary currentStep={1}>
        <View justify="center" direction="row">
          <Loader size="large" ariaLabel={__('Loading...')} />
        </View>
      </Layout>
    );

  if (displayNewAccountChoice) {
    return (
      <Layout backgroundColor="surface" showCartSummary currentStep={1}>
        <AccountChoice {...props} />
      </Layout>
    );
  }
  return (
    <Layout showCartSummary currentStep={1}>
      <Presenter {...props} />
    </Layout>
  );
}

DecorateContent.handleRedirect = async (
  args: PreFetchArgs<Props, Params, Query, FullState>,
) => {
  const { dispatch, getState } = args;

  dispatch(setCartUUID(args.params.cartId));

  // Warning: Do not use injected props here.
  // Re-compute the result of these selectors based on the state update
  // of previous higher order actions.

  const redirectToAccountForm = selectShouldRedirectToAccountForm(getState());

  let redirectToReseller = selectShouldRedirectToResellerOwnerId(getState());
  const resellerOwner = selectResellerOwnerId(getState());
  redirectToReseller = !!(redirectToReseller && resellerOwner);

  const { query } = args;
  const redirectToNonMinimal =
    !query.guess && selectRedirectToNonMinimal(getState());

  const locale = localeSelector(getState());

  if (redirectToAccountForm || redirectToReseller || redirectToNonMinimal) {
    const {
      params: { cartId },
    } = args;

    let redirectTo = '';
    if (redirectToAccountForm) {
      // Set the Cart Owner in a lite, fire-and-forget fashion.
      dispatch(setCartOwner({ headers: { 'X-Dry-Run': 0 } })).catch(() => {});

      const sharingId = selectCurrentId(getState());
      const firstPurchaseUrl = selectFirstPurchaseUrl(getState()); // it starts with a `/`
      redirectTo = `/${locale}/${sharingId}${firstPurchaseUrl}`;
    } else if (redirectToReseller) {
      redirectTo = `/${locale}/${resellerOwner}/cart/${cartId}/configure/account-choice`;
    } else if (redirectToNonMinimal) {
      redirectTo = `/${locale}/${redirectToNonMinimal}/cart/${cartId}/configure/account-choice?guess`;
    }

    args.res.redirect(global.__DEV__ ? `/shop${redirectTo}` : redirectTo);
    throw new Error(`Redirect to ${redirectTo}`);
  }
};

export default (compose(
  needsAuth,
  // For prefetch
  withRouter,
  withPageMeta(args => ({
    title: args.__('Choose billing and ownership information - Gandi.net'),
  })),
)(DecorateContent): React.ComponentType<{}>);
